import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Spinner,
  Alert,
  Progress,
  Input,
  InputGroup,
  InputGroupText,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink
} from "reactstrap"
import Chart from "react-apexcharts"
import {
  getRFQVendorPerformance,
  getRFQVendorPerformanceDetails,
  getRFQVendorPerformanceSummaryDetails,
  getRFQVendorTaskProgressDetails,
  exportRFQVendorPerformanceDetails,
  exportRFQVendorPerformanceSummaryDetails,
  formatCurrency,
  formatPercentage,
  getChartColors,
  truncateText
} from "../../../../helpers/rfq_dashboard_api"
import InfoButton from "./InfoButton"

const VendorPerformance = ({ filters, onRefresh }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [sortBy, setSortBy] = useState('completion_rate')
  const [sortOrder, setSortOrder] = useState('desc')
  const [searchTerm, setSearchTerm] = useState('')

  // Modal states
  const [modalOpen, setModalOpen] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalData, setModalData] = useState(null)
  const [modalTitle, setModalTitle] = useState('')
  const [modalType, setModalType] = useState('')
  const [modalVendorId, setModalVendorId] = useState(null)
  const [modalPage, setModalPage] = useState(1)
  const [modalSearch, setModalSearch] = useState('')
  const [modalPerPage, setModalPerPage] = useState(10) // Add per_page state

  // Load vendor performance data
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log("Loading vendor performance with filters:", filters)

      const response = await getRFQVendorPerformance(filters)
      console.log("Vendor performance API response:", response)

      if (response && response.status && response.data) {
        // Ensure we have the required data structure
        const processedData = {
          vendor_performance: response.data.vendor_performance || [],
          top_performers: response.data.top_performers || [],
          summary: response.data.summary || {}
        }

        // Add vendor_id to top_performers if missing (for chart clicks)
        if (processedData.top_performers.length > 0 && processedData.vendor_performance.length > 0) {
          processedData.top_performers = processedData.top_performers.map(topVendor => {
            const fullVendor = processedData.vendor_performance.find(v =>
              v.vendor_name === topVendor.vendor_name
            )

            return {
              ...topVendor,
              vendor_id: fullVendor?.vendor_id || null,
              total_rfqs_assigned: fullVendor?.total_rfqs_assigned || 0,
              completed_rfqs: fullVendor?.completed_rfqs || 0,
              task_completion_rate: fullVendor?.task_completion_rate || 0,
              payment_efficiency: fullVendor?.payment_efficiency || 0,
              bid_acceptance_rate: fullVendor?.bid_acceptance_rate || 0
            }
          })
        }

        setData(processedData)
        console.log("Vendor performance loaded successfully:", processedData)
      } else {
        throw new Error(response?.message || "Invalid API response structure")
      }
    } catch (err) {
      console.error("Vendor performance loading error:", err)
      setError(err.message || "Failed to load vendor performance")

      // Set empty data structure on error
      setData({
        vendor_performance: [],
        top_performers: [],
        summary: {}
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle chart click to open modal
  const handleChartClick = (type, title, vendorId = null) => {
    console.log('handleChartClick called with:', { type, title, vendorId, vendorIdType: typeof vendorId })

    // Safety check: task_progress_details requires vendor_id
    const isValidVendorId = vendorId &&
                           vendorId !== null &&
                           vendorId !== undefined &&
                           vendorId !== '' &&
                           vendorId !== 'null' &&
                           vendorId !== 'undefined' &&
                           !isNaN(vendorId) &&
                           parseInt(vendorId) > 0

    if (type === 'task_progress_details' && !isValidVendorId) {
      console.error('Cannot open task_progress_details modal without valid vendor_id', {
        type,
        title,
        vendorId,
        vendorIdType: typeof vendorId,
        isValidVendorId
      })
      setError('Task progress details requires a specific vendor selection.')
      return
    }

    console.log('Opening modal with valid vendor_id:', vendorId)
    setModalType(type)
    setModalTitle(title)
    setModalVendorId(vendorId)
    setModalPage(1)
    setModalSearch('')
    setModalPerPage(10) // Reset to default
    setModalOpen(true)
    loadModalData(type, vendorId, 1, '', 10)
  }

  // Handle summary card click to open modal
  const handleSummaryCardClick = (type, title) => {
    setModalType(type)
    setModalTitle(title)
    setModalVendorId(null) // Summary cards show all vendors
    setModalPage(1)
    setModalSearch('')
    setModalPerPage(10) // Reset to default
    setModalOpen(true)
    loadSummaryModalData(type, 1, '', 10)
  }

  // Load modal data
  const loadModalData = async (type, vendorId, page = 1, search = '', perPage = modalPerPage) => {
    try {
      setModalLoading(true)

      console.log('loadModalData called with:', {
        type,
        vendorId,
        vendorIdType: typeof vendorId,
        page,
        search,
        perPage,
        filters
      })

      // Validate that task_progress_details type requires vendor_id
      const isValidVendorId = vendorId &&
                             vendorId !== null &&
                             vendorId !== undefined &&
                             vendorId !== '' &&
                             vendorId !== 'null' &&
                             vendorId !== 'undefined' &&
                             !isNaN(vendorId) &&
                             parseInt(vendorId) > 0

      if (type === 'task_progress_details' && !isValidVendorId) {
        console.error('task_progress_details type requires a valid vendor_id, but got:', {
          vendorId,
          vendorIdType: typeof vendorId,
          isValidVendorId,
          modalState: { modalType, modalVendorId, modalTitle }
        })

        // Close the modal and show error
        setModalOpen(false)
        setModalData(null)
        setError('Invalid modal state: Task progress details requires a specific vendor.')
        return
      }

      const params = {
        type: type,
        page: page,
        per_page: perPage,
        search: search,
        ...filters  // Add main dashboard filters first
      }

      // For task_progress_details, always use the row-specific vendor_id (not dashboard filter vendor_id)
      if (type === 'task_progress_details' && vendorId) {
        params.vendor_id = vendorId  // Override any vendor_id from dashboard filters
      } else if (vendorId) {
        params.vendor_id = vendorId  // For other types, use the provided vendor_id
      }

      console.log('API call params:', params)

      let response;
      if (type === 'task_progress_details') {
        // Use specialized task progress API (vendor_id is already validated above)
        response = await getRFQVendorTaskProgressDetails(params)
      } else {
        // Use regular performance details API
        response = await getRFQVendorPerformanceDetails(params)
      }

      if (response && response.status) {
        setModalData(response.data)
      } else {
        throw new Error(response?.message || "Failed to load modal data")
      }
    } catch (err) {
      console.error("Modal data loading error:", err)
      setModalData(null)
    } finally {
      setModalLoading(false)
    }
  }

  // Load summary modal data for card clicks
  const loadSummaryModalData = async (type, page = 1, search = '', perPage = modalPerPage) => {
    try {
      setModalLoading(true)

      const params = {
        type: type,
        page: page,
        per_page: perPage,
        search: search,
        ...filters  // Add main dashboard filters
      }

      const response = await getRFQVendorPerformanceSummaryDetails(params)

      if (response && response.status) {
        setModalData(response.data)
      } else {
        throw new Error(response?.message || "Failed to load summary modal data")
      }
    } catch (err) {
      console.error("Summary modal data loading error:", err)
      setModalData(null)
    } finally {
      setModalLoading(false)
    }
  }

  // Handle modal pagination
  const handleModalPageChange = (page) => {
    setModalPage(page)

    // Safety check: task_progress_details requires vendor_id
    if (modalType === 'task_progress_details' && (!modalVendorId || modalVendorId === null)) {
      console.error('Cannot paginate task_progress_details without vendor_id')
      closeModal()
      return
    }

    if (modalVendorId !== null) {
      // Chart click modal (specific vendor)
      loadModalData(modalType, modalVendorId, page, modalSearch, modalPerPage)
    } else {
      // Summary card modal (all vendors)
      loadSummaryModalData(modalType, page, modalSearch, modalPerPage)
    }
  }

  // Handle modal search
  const handleModalSearch = (searchValue) => {
    setModalSearch(searchValue)
    setModalPage(1)

    // Safety check: task_progress_details requires vendor_id
    if (modalType === 'task_progress_details' && (!modalVendorId || modalVendorId === null)) {
      console.error('Cannot search task_progress_details without vendor_id')
      closeModal()
      return
    }

    if (modalVendorId !== null) {
      // Chart click modal (specific vendor)
      loadModalData(modalType, modalVendorId, 1, searchValue, modalPerPage)
    } else {
      // Summary card modal (all vendors)
      loadSummaryModalData(modalType, 1, searchValue, modalPerPage)
    }
  }

  // Handle per page change
  const handleModalPerPageChange = (perPage) => {
    setModalPerPage(perPage)
    setModalPage(1)

    // Safety check: task_progress_details requires vendor_id
    if (modalType === 'task_progress_details' && (!modalVendorId || modalVendorId === null)) {
      console.error('Cannot change per page for task_progress_details without vendor_id')
      closeModal()
      return
    }

    if (modalVendorId !== null) {
      // Chart click modal (specific vendor)
      loadModalData(modalType, modalVendorId, 1, modalSearch, perPage)
    } else {
      // Summary card modal (all vendors)
      loadSummaryModalData(modalType, 1, modalSearch, perPage)
    }
  }

  // Handle modal export
  const handleModalExport = async () => {
    try {
      console.log("Export function called with modal state:", {
        modalType,
        modalVendorId,
        modalVendorIdType: typeof modalVendorId,
        modalSearch,
        modalTitle,
        filters
      })

      if (modalVendorId !== null) {
        // Chart click modal export (specific vendor)

        // Additional validation for task_progress_details
        if (modalType === 'task_progress_details') {
          const isValidVendorId = modalVendorId &&
                                 modalVendorId !== null &&
                                 modalVendorId !== undefined &&
                                 modalVendorId !== '' &&
                                 modalVendorId !== 'null' &&
                                 modalVendorId !== 'undefined' &&
                                 !isNaN(modalVendorId) &&
                                 parseInt(modalVendorId) > 0

          if (!isValidVendorId) {
            console.error('Invalid vendor_id for task_progress_details export:', {
              modalVendorId,
              modalVendorIdType: typeof modalVendorId,
              isValidVendorId
            })
            alert(`Cannot export task progress details: Invalid vendor ID (${modalVendorId}). Please try clicking on the task progress column again.`)
            return
          }
        }

        // For task_progress_details, we need to preserve the modalVendorId and not let filters override it
        const dashboardFilters = { ...(filters || {}) }

        // Remove vendor_id from dashboard filters for task_progress_details to prevent override
        if (modalType === 'task_progress_details') {
          delete dashboardFilters.vendor_id
        }

        const params = {
          type: modalType,
          vendor_id: modalVendorId,  // This should not be overridden
          search: modalSearch,
          ...dashboardFilters  // Add other dashboard filters but not vendor_id for task_progress_details
        }


        await exportRFQVendorPerformanceDetails(params)
      } else {
        // Summary card modal export (all vendors)
        const params = {
          type: modalType,
          search: modalSearch,
          ...(filters || {})  // Add main dashboard filters with fallback
        }
        console.log("Calling exportRFQVendorPerformanceSummaryDetails with params:", params)
        await exportRFQVendorPerformanceSummaryDetails(params)
      }
    } catch (err) {
      console.error("Export error:", err)
      alert("Export failed. Please try again.")
    }
  }

  // Close modal
  const closeModal = () => {
    setModalOpen(false)
    setModalData(null)
    setModalType('')
    setModalVendorId(null)
    setModalPage(1)
    setModalSearch('')
    setModalPerPage(10) // Reset to default
  }

  // Load data when filters change
  useEffect(() => {
    // Clear any previous errors
    setError(null)

    loadData()

    // Close any open modals when filters change to prevent stale state
    if (modalOpen) {
      console.log('Filters changed, closing modal to prevent stale state')
      setModalOpen(false)
      setModalData(null)
      setModalType('')
      setModalVendorId(null)
      setModalPage(1)
      setModalSearch('')
      setModalPerPage(10)
    }
  }, [filters])

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-3">
          <h5>Loading Vendor Performance...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert color="danger" className="text-center">
        <h4 className="alert-heading">Error Loading Vendor Performance</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bx bx-refresh me-1"></i>
          Try Again
        </button>
      </Alert>
    )
  }

  if (!data || !data.vendor_performance) {
    return (
      <div className="text-center py-5">
        <i className="bx bx-user-check display-4 text-muted"></i>
        <h5 className="mt-3">No Vendor Performance Data Available</h5>
        <p className="text-muted">Please adjust your filters and try again.</p>
      </div>
    )
  }

  const { vendor_performance, top_performers } = data

  // Filter and sort vendors
  const filteredVendors = vendor_performance
    .filter(vendor =>
      vendor.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.vendor_code.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = parseFloat(a[sortBy]) || 0
      const bValue = parseFloat(b[sortBy]) || 0
      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue
    })



  // Prepare top performers donut chart
  const topPerformersChartOptions = {
    chart: {
      type: 'donut',
      height: 350,
      events: {
        dataPointSelection: function(event, chartContext, config) {
          const selectedVendor = top_performers[config.dataPointIndex]
          if (selectedVendor) {
            handleChartClick(
              'rfqs_assigned',
              `${selectedVendor.vendor_name} - RFQs Assigned`,
              selectedVendor.vendor_id
            )
          }
        }
      }
    },
    labels: top_performers.map(vendor => truncateText(vendor.vendor_name, 15)),
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const vendor = top_performers[opts.seriesIndex]
        return vendor ? `${val.toFixed(1)}%` : val
      },
      style: {
        fontSize: '12px',
        fontWeight: 'bold'
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontWeight: 600,
              color: '#373d3f',
              offsetY: -10
            },
            value: {
              show: true,
              fontSize: '20px',
              fontWeight: 700,
              color: '#373d3f',
              offsetY: 10,
              formatter: function (val) {
                return val + '%'
              }
            },
            total: {
              show: true,
              showAlways: false,
              label: 'Average',
              fontSize: '14px',
              fontWeight: 600,
              color: '#373d3f',
              formatter: function (w) {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                const avg = total / w.globals.seriesTotals.length
                return avg.toFixed(1) + '%'
              }
            }
          }
        }
      }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      markers: {
        width: 8,
        height: 8
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      y: {
        formatter: function (val, opts) {
          const vendor = top_performers[opts.seriesIndex]
          return `${val.toFixed(1)}% completion rate<br/>
                  <small>RFQs: ${vendor?.total_rfqs_assigned || 0} assigned, ${vendor?.completed_rfqs || 0} completed</small><br/>
                  <small>Click to view details</small>`
        }
      }
    },
    colors: getChartColors(),
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          height: 300
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  }

  const topPerformersChartSeries = top_performers.map(vendor => parseFloat(vendor.completion_rate) || 0)

  // Performance comparison column chart
  const performanceMetricsOptions = {
    chart: {
      type: 'column',
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      },
      events: {
        dataPointSelection: function(event, chartContext, config) {
          const selectedVendor = top_performers.slice(0, 3)[config.seriesIndex]
          const metricTypes = ['rfqs_completed', 'task_progress_details', 'payment_efficiency', 'bid_acceptance']
          const metricNames = ['Completion Rate', 'Task Completion', 'Payment Efficiency', 'Bid Acceptance']



          if (selectedVendor) {
            // Ensure we have a valid vendor_id
            let vendorId = selectedVendor.vendor_id
            if (!vendorId || vendorId === null || vendorId === undefined) {
              // Try to find vendor_id from the original vendor_performance data
              const originalVendor = vendor_performance.find(v =>
                v.vendor_name === selectedVendor.vendor_name
              )
              vendorId = originalVendor?.vendor_id
            }

            if (!vendorId || vendorId === null || vendorId === undefined) {
              alert('Cannot view details: Vendor ID not found. Please refresh the page and try again.')
              return
            }

            handleChartClick(
              metricTypes[config.dataPointIndex],
              `${selectedVendor.vendor_name} - ${metricNames[config.dataPointIndex]}`,
              vendorId
            )
          }
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: 'rounded',
        borderRadius: 4,
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + '%'
      },
      offsetY: -20,
      style: {
        fontSize: '10px',
        colors: ['#304758']
      }
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: ['Completion Rate', 'Task Completion', 'Payment Efficiency', 'Bid Acceptance'],
      labels: {
        style: {
          fontSize: '11px',
          fontWeight: 500
        }
      }
    },
    yaxis: {
      title: {
        text: 'Performance (%)',
        style: {
          fontSize: '12px',
          fontWeight: 600
        }
      },
      labels: {
        formatter: function (val) {
          return val.toFixed(0) + '%'
        }
      },
      max: 100
    },
    fill: {
      opacity: 0.8,
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: false,
        opacityFrom: 0.85,
        opacityTo: 0.55,
        stops: [50, 0, 100]
      }
    },
    tooltip: {
      y: {
        formatter: function (val, opts) {
          const vendor = top_performers.slice(0, 3)[opts.seriesIndex]
          const metricNames = ['Completion Rate', 'Task Completion', 'Payment Efficiency', 'Bid Acceptance']
          return `${val.toFixed(1)}% ${metricNames[opts.dataPointIndex]}<br/>
                  <small>Vendor: ${vendor?.vendor_name}</small><br/>
                  <small>Click to view details</small>`
        }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      floating: false,
      fontSize: '12px',
      markers: {
        width: 8,
        height: 8
      }
    },
    colors: getChartColors(),
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          height: 300
        },
        plotOptions: {
          bar: {
            columnWidth: '80%'
          }
        },
        dataLabels: {
          enabled: false
        }
      }
    }]
  }

  const performanceMetricsSeries = top_performers.slice(0, 3).map(vendor => ({
    name: truncateText(vendor.vendor_name, 12),
    data: [
      parseFloat(vendor.completion_rate) || 0,
      parseFloat(vendor.task_completion_rate) || 0,
      parseFloat(vendor.payment_efficiency) || 0,
      parseFloat(vendor.bid_acceptance_rate) || 0
    ]
  }))

  // Get performance badge color
  const getPerformanceBadge = (rate) => {
    if (rate >= 90) return { color: 'success', text: 'Excellent' }
    if (rate >= 75) return { color: 'primary', text: 'Good' }
    if (rate >= 60) return { color: 'warning', text: 'Average' }
    return { color: 'danger', text: 'Poor' }
  }

  return (
    <div className="vendor-performance fade-in">
      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
        .hover-highlight:hover {
          background-color: #f8f9fa;
          border-color: #007bff;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,123,255,0.15);
        }
        .hover-highlight:hover .text-primary {
          color: #0056b3 !important;
        }
        .hover-highlight:hover .text-success {
          color: #155724 !important;
        }
        .hover-highlight:hover .text-info {
          color: #0c5460 !important;
        }
        .hover-highlight:hover .text-warning {
          color: #856404 !important;
        }
        .dashboard-card {
          transition: all 0.3s ease;
        }
      `}</style>

      {/* Performance Summary Cards */}
      {data && data.summary && (
        <div className="summary-grid">
          <Card
            className="metric-card clickable-card"
            style={{ cursor: 'pointer', position: 'relative' }}
            onClick={() => handleSummaryCardClick('total_vendors', 'All Vendors Overview')}
            title="Click to view all vendors"
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="vendor_performance"
                title="Total Vendors - Business Logic & Calculation"
                size="sm"
              />
            </div>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{data.summary.total_vendors || 0}</div>
                <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                  Total Vendors
                  <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                    <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                  </small>
                </div>
              </div>
              <div className="metric-icon">
                <i className="bx bx-group"></i>
              </div>
            </div>
          </Card>

          <Card
            className="metric-card success clickable-card"
            style={{ cursor: 'pointer', position: 'relative' }}
            onClick={() => handleSummaryCardClick('avg_completion_rate', 'Vendors by Completion Rate')}
            title="Click to view vendors ordered by completion rate"
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="vendor_performance"
                title="Avg Completion Rate - Business Logic & Calculation"
                size="sm"
              />
            </div>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{formatPercentage(data.summary.avg_completion_rate)}</div>
                <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                  Avg Completion Rate
                  <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                    <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                  </small>
                </div>
              </div>
              <div className="metric-icon">
                <i className="bx bx-check-circle"></i>
              </div>
            </div>
          </Card>

          <Card
            className="metric-card info clickable-card"
            style={{ cursor: 'pointer', position: 'relative' }}
            onClick={() => handleSummaryCardClick('avg_task_completion', 'Vendors by Task Completion')}
            title="Click to view vendors ordered by task completion rate"
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="vendor_performance"
                title="Avg Task Completion - Business Logic & Calculation"
                size="sm"
              />
            </div>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{formatPercentage(data.summary.avg_task_completion_rate)}</div>
                <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                  Avg Task Completion
                  <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                    <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                  </small>
                </div>
              </div>
              <div className="metric-icon">
                <i className="bx bx-task"></i>
              </div>
            </div>
          </Card>

          <Card
            className="metric-card warning clickable-card"
            style={{ cursor: 'pointer', position: 'relative' }}
            onClick={() => handleSummaryCardClick('avg_payment_efficiency', 'Vendors by Payment Efficiency')}
            title="Click to view vendors ordered by payment efficiency"
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="vendor_performance"
                title="Avg Payment Efficiency - Business Logic & Calculation"
                size="sm"
              />
            </div>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{formatPercentage(data.summary.avg_payment_efficiency)}</div>
                <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                  Avg Payment Efficiency
                  <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                    <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                  </small>
                </div>
              </div>
              <div className="metric-icon">
                <i className="bx bx-money"></i>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Performance Overview Charts */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <div>
                  <i className="bx bx-pie-chart-alt-2 me-2"></i>
                  Top Performing Vendors by Completion Rate
                  <small className="text-muted d-block mt-1">Click on segments to view vendor details</small>
                </div>
                <InfoButton
                  type="vendor_performance"
                  title="Top Performing Vendors Chart - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              {top_performers && top_performers.length > 0 ? (
                <Chart
                  options={topPerformersChartOptions}
                  series={topPerformersChartSeries}
                  type="donut"
                  height={396}
                />
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-trophy display-4 text-muted"></i>
                  <p className="text-muted mt-2">No top performers data available</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <div>
                  <i className="bx bx-bar-chart-alt-2 me-2"></i>
                  Multi-Metric Performance Comparison
                  <small className="text-muted d-block mt-1">Click on bars to view detailed records</small>
                </div>
                <InfoButton
                  type="vendor_performance"
                  title="Multi-Metric Performance Chart - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              {performanceMetricsSeries.length > 0 ? (
                <Chart
                  options={performanceMetricsOptions}
                  series={performanceMetricsSeries}
                  type="bar"
                  height={350}
                />
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-bar-chart display-4 text-muted"></i>
                  <p className="text-muted mt-2">No comparison data available</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      

      {/* Vendor Performance Table */}
      <Row>
        <Col lg={12}>
          <Card className="dashboard-card">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <CardTitle className="mb-0 d-flex align-items-center">
                  <span className="me-2">
                    <i className="bx bx-table me-2"></i>
                    Vendor Performance Details
                  </span>
                  <InfoButton
                    type="vendor_performance"
                    title="Vendor Performance Table - Calculation Logic"
                    size="sm"
                  />
                </CardTitle>
                <div className="d-flex gap-3">
                  <FormGroup className="mb-0">
                    <Input
                      type="text"
                      placeholder="Search vendors..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: '200px' }}
                    />
                  </FormGroup>
                  {/*<FormGroup className="mb-0">
                    <Input
                      type="select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      style={{ width: '150px' }}
                    >
                      <option value="completion_rate">Completion Rate</option>
                      <option value="task_completion_rate">Task Completion</option>
                      <option value="payment_efficiency">Payment Efficiency</option>
                      <option value="bid_acceptance_rate">Bid Acceptance</option>
                      <option value="total_contract_value">Contract Value</option>
                    </Input>
                  </FormGroup>*/}
                  {/*<FormGroup className="mb-0">
                    <Input
                      type="select"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      style={{ width: '100px' }}
                    >
                      <option value="desc">High to Low</option>
                      <option value="asc">Low to High</option>
                    </Input>
                  </FormGroup>*/}
                </div>
              </div>

              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>RFQs Assigned</th>
                      <th>Completion Rate</th>
                      <th>Task Performance</th>
                      <th>Payment Efficiency</th>
                      <th>Bid Acceptance</th>
                      {/*<th>Contract Value</th>
                      <th>Performance</th>*/}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((vendor, index) => {
                      const performanceBadge = getPerformanceBadge(vendor.completion_rate)
                      return (
                        <tr key={index}>
                          <td>
                            <div>
                              <h6 className="mb-0">{vendor.vendor_name}</h6>
                              <small className="text-muted">{vendor.vendor_code}</small>
                              <br />
                              <small className="text-muted">
                                {vendor.email_address} • {vendor.mobile_no}
                              </small>
                            </div>
                          </td>
                          <td>
                            <div
                              className="cursor-pointer hover-highlight"
                              onClick={() => handleChartClick('rfqs_assigned', `${vendor.vendor_name} - RFQs Assigned`, vendor.vendor_id)}
                              title="Click to view RFQ details"
                            >
                              <strong>{vendor.total_rfqs_assigned}</strong>
                              <small className="text-muted d-block">
                                <span
                                  className="cursor-pointer text-primary"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleChartClick('rfqs_completed', `${vendor.vendor_name} - Completed RFQs`, vendor.vendor_id)
                                  }}
                                >
                                  {vendor.completed_rfqs} completed
                                </span>
                              </small>
                            </div>
                          </td>
                          <td>
                            <div
                              className="d-flex align-items-center cursor-pointer hover-highlight"
                              onClick={() => handleChartClick('rfqs_completed', `${vendor.vendor_name} - Completion Rate`, vendor.vendor_id)}
                              title="Click to view completion details"
                            >
                              <div className="flex-grow-1">
                                <Progress
                                  value={vendor.completion_rate}
                                  color={performanceBadge.color}
                                  className="progress-sm"
                                />
                              </div>
                              <div className="flex-shrink-0 ms-2">
                                <small>{formatPercentage(vendor.completion_rate)}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div
                              className="cursor-pointer hover-highlight"
                              onClick={() => {
                                // Ensure we have a valid vendor_id
                                let vendorId = vendor.vendor_id
                                if (!vendorId || vendorId === null || vendorId === undefined) {
                                  // Try to find vendor_id from the original vendor_performance data
                                  const originalVendor = data.vendor_performance.find(v =>
                                    v.vendor_name === vendor.vendor_name && v.vendor_code === vendor.vendor_code
                                  )
                                  vendorId = originalVendor?.vendor_id
                                }

                                if (!vendorId || vendorId === null || vendorId === undefined) {
                                  alert('Cannot view task details: Vendor ID not found. Please refresh the page and try again.')
                                  return
                                }

                                handleChartClick('task_progress_details', `${vendor.vendor_name} - Task Performance`, vendorId)
                              }}
                              title="Click to view task details"
                            >
                              <strong>{formatPercentage(vendor.task_completion_rate)}</strong>
                              <small className="text-muted d-block">
                                <span
                                  className="cursor-pointer text-primary"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleChartClick('tasks_completed', `${vendor.vendor_name} - Completed Tasks`, vendor.vendor_id)
                                  }}
                                >
                                  {vendor.completed_tasks}/{vendor.total_tasks} tasks
                                </span>
                              </small>
                            </div>
                          </td>
                          <td>
                            <div
                              className="cursor-pointer hover-highlight"
                              onClick={() => handleChartClick('payments_received', `${vendor.vendor_name} - Payment Efficiency`, vendor.vendor_id)}
                              title="Click to view payment details"
                            >
                              <strong>{formatPercentage(vendor.payment_efficiency)}</strong>
                              <small className="text-muted d-block">
                                {formatCurrency(vendor.total_payments_received)}
                              </small>
                            </div>
                          </td>
                          <td>
                            <div
                              className="cursor-pointer hover-highlight"
                              onClick={() => handleChartClick('bids_accepted', `${vendor.vendor_name} - Bid Acceptance`, vendor.vendor_id)}
                              title="Click to view bid details"
                            >
                              <strong>{formatPercentage(vendor.bid_acceptance_rate)}</strong>
                              <small className="text-muted d-block">
                                <span className="text-success">{vendor.accepted_bids} accepted</span> /
                                <span
                                  className="cursor-pointer text-danger ms-1"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleChartClick('bids_rejected', `${vendor.vendor_name} - Rejected Bids`, vendor.vendor_id)
                                  }}
                                >
                                  {vendor.rejected_bids} rejected
                                </span>
                              </small>
                            </div>
                          </td>
                          {/*<td>
                            <strong>{formatCurrency(vendor.total_contract_value)}</strong>
                          </td>
                          <td>
                            <Badge color={performanceBadge.color}>
                              {performanceBadge.text}
                            </Badge>
                          </td>*/}
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>

              {filteredVendors.length === 0 && (
                <div className="text-center py-4">
                  <i className="bx bx-search display-4 text-muted"></i>
                  <h5 className="mt-3">No vendors found</h5>
                  <p className="text-muted">Try adjusting your search criteria.</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Details Modal */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="xl">
        <ModalHeader toggle={closeModal}>
          <i className="bx bx-detail me-2"></i>
          {modalTitle}
        </ModalHeader>
        <ModalBody>
          {/* Search and Controls - Always visible like Overview tab */}
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroupText>
                  <i className="bx bx-search"></i>
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search by RFQ#, title, project, vendor, or status..."
                  value={modalSearch}
                  onChange={(e) => handleModalSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4}></Col>
            <Col md={2}>
              <div className="d-flex align-items-center">
                <span className="me-2 text-nowrap">Show:</span>
                <Input
                  type="select"
                  id="recordsPerPage"
                  value={modalPerPage}
                  onChange={(e) => handleModalPerPageChange(parseInt(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </div>
            </Col>
            <Col md={2} className="text-end">
              <Button
                color="success"
                onClick={handleModalExport}
                block
              >
                <i className="bx bx-download me-1"></i>
                Export CSV
              </Button>
            </Col>
          </Row>

          {/* Data section with loading state */}
          {modalLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading details...</p>
            </div>
          ) : modalData ? (
            <>
              {/* Records Table */}
              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead>
                    <tr>
                      {/* Summary card modals show vendor data */}
                      {['total_vendors', 'avg_completion_rate', 'avg_task_completion', 'avg_payment_efficiency'].includes(modalType) ? (
                        <>
                          <th>Vendor Name</th>
                          <th>Vendor Code</th>
                          <th>Contact</th>
                          <th>RFQs Assigned</th>
                          {modalType !== 'total_vendors' && <th>Completion Rate</th>}
                          {modalType === 'avg_task_completion' && <th>Task Progress</th>}
                          {modalType === 'avg_payment_efficiency' && <th>Payment Efficiency</th>}
                          <th>Actions</th>
                        </>
                      ) : modalType === 'task_progress_details' ? (
                        <>
                          <th>Task Title</th>
                          <th>RFQ</th>
                          <th>Project</th>
                          <th>Task Status</th>
                          <th>Work Progress</th>
                          <th>Timeline</th>
                          <th>Last Updated</th>
                        </>
                      ) : (
                        <>
                          <th>RFQ#</th>
                          <th>Title</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Budget Amount</th>
                          <th>Final Amount</th>
                          <th>Status</th>
                          {modalType.includes('task') && <th>Task</th>}
                          {(modalType.includes('bid') || modalType === 'bid_acceptance') && <th>Proposal Amount</th>}
                          {(modalType.includes('bid') || modalType === 'bid_acceptance') && <th>Work Timeline</th>}
                          {modalType === 'bid_acceptance' && <th>Bid Status</th>}
                          {modalType === 'bids_rejected' && <th>Reject Reason</th>}
                          {(modalType.includes('payment') || modalType === 'payment_efficiency') && <th>Payment Details</th>}
                          {(modalType.includes('payment') ||modalType === 'payment_efficiency') && <th>Payment Status</th>}

                          {modalType.includes('milestone') && <th>Milestone</th>}
                          {modalType.includes('invoice') && <th>Invoice</th>}
                          <th>Estimated Date</th>
                          <th>Completion Date</th>
                          <th>Created Date</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.records && modalData.records.length > 0 ? (
                      modalData.records.map((record, index) => (
                        <tr key={index}>
                          {/* Summary card modals show vendor data */}
                          {['total_vendors', 'avg_completion_rate', 'avg_task_completion', 'avg_payment_efficiency'].includes(modalType) ? (
                            <>
                              <td>
                                <div>
                                  <strong>{record.vendor_name}</strong>
                                  <div>
                                    <small className="text-muted">{record.vendor_code}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <Badge color="secondary" className="badge-soft">
                                  {record.vendor_code}
                                </Badge>
                              </td>
                              <td>
                                <div>
                                  <small>{record.email_address}</small>
                                  <div>
                                    <small className="text-muted">{record.mobile_no}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="text-center">
                                  <strong className="text-primary">{record.total_rfqs_assigned}</strong>
                                  <div>
                                    <small className="text-success">{record.completed_rfqs} completed</small>
                                  </div>
                                </div>
                              </td>
                              {modalType !== 'total_vendors' && (
                                <td>
                                  <div className="text-center">
                                    <Badge
                                      color={
                                        record.completion_rate >= 80 ? 'success' :
                                        record.completion_rate >= 60 ? 'warning' : 'danger'
                                      }
                                      className="badge-soft"
                                    >
                                      {formatPercentage(record.completion_rate)}
                                    </Badge>
                                  </div>
                                </td>
                              )}
                              {modalType === 'avg_task_completion' && (
                                <td>
                                  <div className="text-center">
                                    <div className="mb-2">
                                      <strong className="text-info">{formatPercentage(record.task_completion_rate)}</strong>
                                      <small className="text-muted d-block">
                                        {record.completed_tasks}/{record.total_tasks} tasks completed
                                      </small>
                                    </div>
                                    <div className="mb-2">
                                      <div className="d-flex align-items-center justify-content-center">
                                        <div className="progress" style={{width: '60px', height: '8px'}}>
                                          <div
                                            className="progress-bar bg-success"
                                            style={{width: `${Math.min(record.avg_work_progress || 0, 100)}%`}}
                                          ></div>
                                        </div>
                                        <small className="text-success ms-2">
                                          {formatPercentage(record.avg_work_progress)} avg progress
                                        </small>
                                      </div>
                                    </div>
                                    {record.total_progress_entries > 0 && (
                                      <div>
                                        <Badge color="info" className="badge-soft">
                                          {record.total_progress_entries} updates
                                        </Badge>
                                        {record.latest_progress_update && (
                                          <div>
                                            <small className="text-muted">
                                              Last: {new Date(record.latest_progress_update).toLocaleDateString()}
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              )}
                              {modalType === 'avg_payment_efficiency' && (
                                <td>
                                  <div className="text-center">
                                    <strong className="text-warning">{formatPercentage(record.payment_efficiency)}</strong>
                                    <div>
                                      <small className="text-muted">
                                        {formatCurrency(record.total_payments_received)} received
                                      </small>
                                    </div>
                                  </div>
                                </td>
                              )}
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleChartClick('rfqs_assigned', `${record.vendor_name} - RFQs`, record.vendor_id)}
                                  >
                                    <i className="bx bx-detail me-1"></i>
                                    View RFQs
                                  </Button>
                                  {modalType === 'avg_task_completion' && record.vendor_id && record.vendor_id !== null && record.vendor_id !== 'null' && !isNaN(record.vendor_id) && parseInt(record.vendor_id) > 0 && (
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => handleChartClick('task_progress_details', `${record.vendor_name} - Task Progress`, record.vendor_id)}
                                    >
                                      <i className="bx bx-task me-1"></i>
                                      Tasks
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </>
                          ) : modalType === 'task_progress_details' ? (
                            <>
                              <td>
                                <div>
                                  <strong>{record.task_title}</strong>
                                  {record.task_description && (
                                    <div>
                                      <small className="text-muted">
                                        {truncateText(record.task_description, 50)}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  <strong>{record.rfq_code}</strong>
                                  <div>
                                    <small className="text-muted">
                                      {truncateText(record.rfq_title, 30)}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <small>{record.project_name}</small>
                              </td>
                              <td>
                                <Badge
                                  color={
                                    record.task_status === 'completed' ? 'success' :
                                    record.task_status === 'in_progress' ? 'warning' :
                                    record.task_status === 'pending' ? 'secondary' : 'info'
                                  }
                                  className="badge-soft"
                                >
                                  {record.task_status ? record.task_status.replace('_', ' ').toUpperCase() : 'PENDING'}
                                </Badge>
                              </td>
                              <td>
                                <div className="text-center">
                                  <div className="d-flex align-items-center justify-content-center mb-1">
                                    <div className="progress" style={{width: '80px', height: '10px'}}>
                                      <div
                                        className="progress-bar bg-success"
                                        style={{width: `${Math.min(record.progress_percentage || 0, 100)}%`}}
                                      ></div>
                                    </div>
                                    <small className="text-success ms-2">
                                      {record.progress_percentage || 0}%
                                    </small>
                                  </div>
                                  {record.progress_description && (
                                    <div>
                                      <small className="text-muted">
                                        {truncateText(record.progress_description, 40)}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {record.work_start && (
                                    <small className="text-muted d-block">
                                      Start: {new Date(record.work_start).toLocaleDateString()}
                                    </small>
                                  )}
                                  {record.work_end && (
                                    <small className="text-muted d-block">
                                      End: {new Date(record.work_end).toLocaleDateString()}
                                    </small>
                                  )}
                                  {record.estimated_completion_date && (
                                    <small className="text-info d-block">
                                      Est: {new Date(record.estimated_completion_date).toLocaleDateString()}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>
                                {record.progress_updated_at ? (
                                  <small className="text-muted">
                                    {new Date(record.progress_updated_at).toLocaleDateString()}
                                    <br />
                                    {new Date(record.progress_updated_at).toLocaleTimeString()}
                                  </small>
                                ) : (
                                  <small className="text-muted">No updates</small>
                                )}
                              </td>
                            </>
                          ) : (
                            <>
                              <td>
                                <strong>{record.rfq_number}</strong>
                              </td>
                              <td>{truncateText(record.title, 30)}</td>
                              <td>{record.project_name}</td>
                              <td>{record.vendor_name}</td>
                              <td>
                                <div className="text-end">
                                  {record.budget ? (
                                    <strong className="text-primary">
                                      ₹{parseFloat(record.budget).toLocaleString('en-IN', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2
                                      })}
                                    </strong>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="text-end">
                                  {record.final_amount ? (
                                    <strong className="text-success">
                                      ₹{parseFloat(record.final_amount).toLocaleString('en-IN', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2
                                      })}
                                    </strong>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Badge color="info" className="badge-soft-info">
                                  {record.status}
                                </Badge>
                              </td>
                          {modalType.includes('task') && (
                            <td>
                              <div>
                                <small>{truncateText(record.task, 25)}</small>
                                {record.task_status && (
                                  <Badge
                                    color={record.task_status === 'completed' ? 'success' : 'warning'}
                                    className="badge-soft ms-1"
                                  >
                                    {record.task_status}
                                  </Badge>
                                )}
                              </div>
                            </td>
                          )}
                          {(modalType.includes('bid') || modalType === 'bid_acceptance') && (
                            <td>
                              {record.proposal_amount ? formatCurrency(record.proposal_amount) : '-'}
                            </td>
                          )}
                          {(modalType.includes('bid') || modalType === 'bid_acceptance') && (
                            <td>
                              <small>
                                {record.work_timeline
                                  ? new Date(record.work_timeline).toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: '2-digit'
                                    })
                                  : '-'
                                }
                              </small>
                            </td>
                          )}
                          {modalType === 'bid_acceptance' && (
                            <td>
                              {record.combined_status ? (
                                <div>
                                  <Badge
                                    color={
                                      record.combined_status.includes('Accepted') && record.combined_status.includes('Rejected') ? 'warning' :
                                      record.combined_status.includes('Accepted') ? 'success' :
                                      record.combined_status.includes('Rejected') ? 'danger' : 'secondary'
                                    }
                                    className="badge-soft"
                                  >
                                    {record.combined_status}
                                  </Badge>
                                  {record.bid_statuses && (
                                    <div className="mt-1">
                                      <small className="text-muted">
                                        Statuses: {record.bid_statuses}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Fallback to individual status display
                                <div>
                                  {record.is_rfq_vendor_bid_accepted == 1 ? (
                                    <Badge color="success" className="badge-soft-success">
                                      Accepted
                                    </Badge>
                                  ) : record.is_rfq_rejected == 1 ? (
                                    <Badge color="danger" className="badge-soft-danger">
                                      Rejected
                                    </Badge>
                                  ) : (
                                    <Badge color="secondary" className="badge-soft-secondary">
                                      Pending
                                    </Badge>
                                  )}
                                </div>
                              )}
                              {record.reject_reason && (
                                <div className="mt-1">
                                  <small className="text-danger">
                                    Reject Reasons: {record.reject_reason}
                                  </small>
                                </div>
                              )}
                            </td>
                          )}
                          {modalType === 'bids_rejected' && (
                            <td>
                              <small className="text-danger">{record.reject_reason || '-'}</small>
                            </td>
                          )}
                          {(modalType.includes('payment') || modalType === 'payment_efficiency') && (
                            <td>
                              <div>
                                <strong className="text-success">
                                  Paid: {record.paid_amount ? formatCurrency(record.paid_amount) : '₹0'}
                                </strong>
                                {record.amount_requested && (
                                  <div>
                                    <small className="text-muted">
                                      Requested: {formatCurrency(record.amount_requested)}
                                    </small>
                                  </div>
                                )}
                                {record.remaining_amount > 0 && (
                                  <div>
                                    <small className="text-warning">
                                      Remaining: {formatCurrency(record.remaining_amount)}
                                    </small>
                                  </div>
                                )}
                                {record.payment_date && (
                                  <div>
                                    <small className="text-muted">
                                      Payment Date: {new Date(record.payment_date).toLocaleDateString()}
                                    </small>
                                  </div>
                                )}
                              </div>
                            </td>
                          )}
                          {(modalType.includes('payment') || modalType === 'payment_efficiency') && (
                            <td>
                              <Badge
                                color={
                                  record.payment_status === 'paid' ? 'success' :
                                  record.payment_status === 'approved' ? 'info' :
                                  record.payment_status === 'pending' ? 'warning' :
                                  record.payment_status === 'rejected' ? 'danger' : 'secondary'
                                }
                                className="badge-soft"
                              >
                                {record.payment_status ? record.payment_status.charAt(0).toUpperCase() + record.payment_status.slice(1) : 'Unknown'}
                              </Badge>
                              {record.reject_advance_desc && record.payment_status === 'rejected' && (
                                <div className="mt-1">
                                  <small className="text-danger">
                                    Reason: {record.reject_advance_desc}
                                  </small>
                                </div>
                              )}
                              {record.requested_date && (
                                <div className="mt-1">
                                  <small className="text-muted">
                                    Requested: {new Date(record.requested_date).toLocaleDateString()}
                                  </small>
                                </div>
                              )}
                            </td>
                          )}

                          {modalType.includes('milestone') && (
                            <td>
                              <div>
                                <small>{record.milestone_title}</small>
                                <br />
                                <strong className="text-primary">
                                  {formatCurrency(record.payment_amount || 0)}
                                </strong>
                              </div>
                            </td>
                          )}
                          {modalType.includes('invoice') && (
                            <td>
                              <div>
                                <small>{record.invoice_number}</small>
                                <br />
                                <strong className="text-success">
                                  {formatCurrency(record.invoice_amount || 0)}
                                </strong>
                              </div>
                            </td>
                          )}
                              <td>
                                <small className="text-info">
                                  {record.estimated_date || 'N/A'}
                                </small>
                              </td>
                              <td>
                                <small className="text-success">
                                  {record.completion_date ? new Date(record.completion_date).toLocaleDateString() : '-'}
                                </small>
                              </td>
                              <td>
                                <small>{new Date(record.created_at).toLocaleDateString()}</small>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="17" className="text-center py-4">
                          <i className="bx bx-search display-4 text-muted"></i>
                          <p className="text-muted mt-2">No records found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {modalData.records && modalData.records.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <small className="text-muted">
                      {modalData.pagination ? (
                        <>
                          Showing {((modalData.pagination.current_page - 1) * modalData.pagination.per_page) + 1} to{' '}
                          {Math.min(modalData.pagination.current_page * modalData.pagination.per_page, modalData.pagination.total_records)} of{' '}
                          {modalData.pagination.total_records} records
                        </>
                      ) : (
                        <>Showing {modalData.records.length} records</>
                      )}
                    </small>
                  </div>
                  {modalData.pagination && modalData.pagination.total_pages > 1 && (
                    <Pagination className="pagination-sm mb-0">
                      <PaginationItem disabled={!modalData.pagination.has_prev_page}>
                        <PaginationLink
                          previous
                          onClick={() => handleModalPageChange(modalData.pagination.current_page - 1)}
                        />
                      </PaginationItem>

                      {[...Array(Math.min(5, modalData.pagination.total_pages))].map((_, i) => {
                        const pageNum = modalData.pagination.current_page - 2 + i
                        if (pageNum > 0 && pageNum <= modalData.pagination.total_pages) {
                          return (
                            <PaginationItem key={pageNum} active={pageNum === modalData.pagination.current_page}>
                              <PaginationLink onClick={() => handleModalPageChange(pageNum)}>
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        }
                        return null
                      })}

                      <PaginationItem disabled={!modalData.pagination.has_next_page}>
                        <PaginationLink
                          next
                          onClick={() => handleModalPageChange(modalData.pagination.current_page + 1)}
                        />
                      </PaginationItem>
                    </Pagination>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <i className="bx bx-error display-4 text-muted"></i>
              <p className="text-muted mt-2">Failed to load details</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default VendorPerformance
